import { useState } from "react";
import { Trash2, Search, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useWarehouses, useSearchLocations, useRemoveSkuLocation } from "@/hooks/use-warehouse";
import { WarehouseNav } from "@/components/warehouse/warehouse-nav";
import { toast } from "sonner";
import { TablePagination } from "@/components/ui/table-pagination";
import { formatBinLabelForDisplay } from "@/lib/warehouse/bin-label";

export function LocationsPage() {
  const { data: warehouses } = useWarehouses();
  const [inputValue, setInputValue] = useState("");
  const [query, setQuery] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [sort, setSort] = useState<"newest" | "oldest" | "default">("default");
  const [page, setPage] = useState(1);
  const { data, isLoading } = useSearchLocations(query, warehouseId, page, sort);
  const removeMut = useRemoveSkuLocation();

  async function handleRemove(id: string) {
    try {
      await removeMut.mutateAsync(id);
      toast.success("Lokacija obrisana");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri brisanju lokacije");
    }
  }

  return (
    <div className="flex flex-col h-full">
      <WarehouseNav />

      <div className="p-4 flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Skeniraj/pretraži SKU ili lokaciju..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  setQuery(inputValue.trim());
                  setPage(1);
                  setInputValue("");
                }
              }}
              className="h-9 pl-9 flex-1"
            />
          </div>
          <Select value={warehouseId || "all"} onValueChange={(v) => { setWarehouseId(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-full sm:w-48 h-9!">
              <SelectValue placeholder="Sva skladišta" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Sva skladišta</SelectItem>
              {warehouses?.map((w: any) => (
                <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={(v) => { setSort(v as "newest" | "oldest" | "default"); setPage(1); }}>
            <SelectTrigger className="w-full sm:w-44 h-9!">
              <SelectValue placeholder="Sortiraj" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Zadano</SelectItem>
              <SelectItem value="newest">Najnovije</SelectItem>
              <SelectItem value="oldest">Najstarije</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {query && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Pretraga:</span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 ring-1 ring-foreground/10 rounded-sm">
              «{query}»
              <button
                type="button"
                onClick={() => { setQuery(""); setPage(1); }}
                aria-label="Očisti pretragu"
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          </div>
        )}

        {isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : !data?.data?.length ? (
          <p className="text-xs text-muted-foreground py-8 text-center">
            {query ? "Nema rezultata" : "Nema lokacija"}
          </p>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden sm:block ring-1 ring-foreground/10 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="p-2 text-left font-medium">SKU</th>
                    <th className="p-2 text-left font-medium">Lokacija</th>
                    <th className="p-2 text-left font-medium">Skladište</th>
                    <th className="p-2 text-left font-medium">Datum</th>
                    <th className="p-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((loc: any) => (
                    <tr key={loc.id} className="border-b hover:bg-muted/50 transition-colors">
                      <td className="p-2 font-medium">{loc.sku}</td>
                      <td className="p-2 tabular-nums">{formatBinLabelForDisplay(loc.fullLabel)}</td>
                      <td className="p-2 text-muted-foreground">{loc.warehouseName}</td>
                      <td className="p-2 text-muted-foreground tabular-nums">
                        {new Date(loc.createdAt * 1000).toLocaleString("hr", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="p-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemove(loc.id)}
                          disabled={removeMut.isPending}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card layout */}
            <div className="flex flex-col gap-1.5 sm:hidden">
              {data.data.map((loc: any) => (
                <div
                  key={loc.id}
                  className="flex items-center justify-between p-3 ring-1 ring-foreground/10"
                >
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="text-xs font-medium truncate">{loc.sku}</span>
                    <div className="flex gap-2 text-xs text-muted-foreground">
                      <span className="tabular-nums">{formatBinLabelForDisplay(loc.fullLabel)}</span>
                      <span>·</span>
                      <span>{loc.warehouseName}</span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemove(loc.id)}
                    disabled={removeMut.isPending}
                    className="shrink-0"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}

        {data && <TablePagination page={page} totalPages={Math.ceil(data.total / 50)} onPageChange={setPage} />}
      </div>
    </div>
  );
}
